import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Alert, App, Button, Form, Input, Skeleton } from 'antd'
import type { z } from 'zod'
import type { contractDocumentDetailSchema } from '@super-mega/server/contracts/contract'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { ContractLexicalEditor, type ContractLexicalContent } from './contract-lexical-editor.tsx'
import { useContractDocumentInvalidation } from './hooks/use-contract-document-invalidation.ts'

type ContractDocument = z.infer<typeof contractDocumentDetailSchema>
type FormValues = { title: string; note?: string }

export function ContractDocumentEditorPage({ documentId }: { documentId: number }) {
  const trpc = useTRPC()
  const detail = useQuery(trpc.contract.document.detail.queryOptions({ id: documentId }))
  return (
    <FormPageContainer breadcrumbRender={false} title="编辑合同文件" subTitle={detail.data?.contractNo}>
      {detail.isError && (
        <Alert
          className="mb-4"
          showIcon
          type="error"
          title={detail.data ? '合同文件刷新失败，未保存内容已保留' : '合同文件加载失败'}
          description={getErrorMessage(detail.error, '请稍后重试')}
          action={<Button onClick={() => void detail.refetch()}>重试</Button>}
        />
      )}
      {detail.data && <ContractDocumentForm key={documentId} document={detail.data} />}
      {!detail.data && !detail.isError && <Skeleton active />}
    </FormPageContainer>
  )
}

function ContractDocumentForm({ document }: { document: ContractDocument }) {
  const trpc = useTRPC()
  const access = useAccess()
  const { message } = App.useApp()
  const invalidate = useContractDocumentInvalidation()
  const [form] = Form.useForm<FormValues>()
  // 编辑会话只初始化一次，后台刷新不能覆盖尚未保存的正文。
  const [content, setContent] = useState(() => document.content as ContractLexicalContent)
  const update = useMutation(trpc.contract.document.updateDraft.mutationOptions())
  const readOnly = document.status !== 'confirmed' || !access.can('contract.document.update')
  const save = async (values: FormValues) => {
    if (readOnly || update.isPending) return
    try {
      await update.mutateAsync({
        id: document.id,
        data: {
          title: values.title.trim(),
          note: values.note?.trim() || null,
          content,
          printSettings: document.printSettings as Record<string, unknown> | null
        }
      })
      void message.success('合同文件已保存')
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '合同文件保存失败'))
    }
  }

  return (
    <Form<FormValues>
      form={form}
      initialValues={{ title: document.title, note: document.note ?? undefined }}
      layout="vertical"
      onFinish={save}
      scrollToFirstError
    >
      {readOnly && <Alert className="mb-4" showIcon type="info" title="当前合同状态或权限不允许编辑，仅可查看" />}
      <section className="bg-surface mb-4 rounded-xl p-6">
        <Form.Item
          label="合同名称"
          name="title"
          rules={[{ required: true, whitespace: true, message: '请输入合同名称' }]}
        >
          <Input disabled={readOnly || update.isPending} maxLength={255} />
        </Form.Item>
        <Form.Item label="备注" name="note">
          <Input.TextArea disabled={readOnly || update.isPending} maxLength={5000} rows={2} showCount />
        </Form.Item>
      </section>
      <section className="bg-surface overflow-hidden rounded-xl p-4">
        <h2 className="mb-4 text-base font-semibold">合同正文</h2>
        <ContractLexicalEditor
          key={String(readOnly)}
          initialValue={content}
          onChange={readOnly ? undefined : setContent}
          readOnly={readOnly}
        />
      </section>
      <div className="border-border bg-surface sticky bottom-0 z-10 mt-4 flex justify-end gap-3 border-t p-4">
        <Link to="/contracts/documents" search={{ current: 1, pageSize: 20 }}>
          返回合同文件
        </Link>
        {!readOnly && (
          <Button htmlType="submit" type="primary" loading={update.isPending}>
            保存合同
          </Button>
        )}
      </div>
    </Form>
  )
}
